import { Button } from '@mgh-app/component-library';
import { PopupDialog } from 'components/PopupDialog';
import { ValidationErrors } from 'components/ValidationErrors';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import OtpInput from 'react-otp-input';
import { localStorageService } from 'services/localStorageService';
import {
  useLazyGetUserDetailsQuery,
  useLazyVerifyEmailAddressQuery,
  VerifyEmailAddressError,
} from 'services/UsersService';
import {
  borders,
  display,
  flexDirection,
  fontSize,
  fontWeight,
  gap,
  justifyContent,
  margin,
  outlineWidth,
  twCls,
} from 'style';

interface Fields {
  otp: string;
}

export const VerifyEmailChange = (): JSX.Element => {
  const { setTitle, showNavigationButtons, showUserProfile, navigateHomeAction, showHomeButton } =
    useContext(MainLayoutContext);
  const { handleSubmit, formState } = useForm<Fields>({
    defaultValues: {
      otp: '',
    },
  });

  const [emailChanged, setEmailChanged] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState(false);

  const [customError, setCustomError] = useState<string[]>();
  const [otp, setOtp] = useState<string>('');

  const [verifyEmailAddress] = useLazyVerifyEmailAddressQuery();
  const [getUserDetails] = useLazyGetUserDetailsQuery();
  const { navigateAccount } = useAppNavigation();

  const verifyEmail = async () => {
    setCustomError([]);
    try {
      await verifyEmailAddress({
        verification_code: otp,
      }).unwrap();
      setEmailChanged(true);

      const userDetails = await getUserDetails().unwrap();
      localStorageService.setUser(userDetails.email);
    } catch (err) {
      const error = err as VerifyEmailAddressError;
      if (error?.message) {
        setEmailVerificationError(true);
        if (Array.isArray(error.message)) {
          setCustomError(error.message);
        } else if (error.message) {
          setCustomError([error.message]);
        } else {
          setCustomError(['Unknown error']);
        }
      } else {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    setTitle('Verify Email');
    showNavigationButtons(true);
    showUserProfile(true);
    showHomeButton(true);
  }, [navigateHomeAction, setTitle, showHomeButton, showNavigationButtons, showUserProfile]);

  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'), margin('mt-10'))}>
      <p className={twCls(margin('mt-16'))}>
        A six digit verification code has been sent to your email address
      </p>
      <OtpInput
        value={otp}
        onChange={setOtp}
        containerStyle={twCls(
          margin('mt-4'),
          display('flex'),
          justifyContent('justify-center'),
          fontSize('text-3xl'),
          fontWeight('font-medium'),
          gap('gap-4'),
        )}
        numInputs={6}
        isInputNum
        inputStyle={twCls(borders('border-b-2'))}
        focusStyle={twCls(outlineWidth('outline-0'))}
      />
      <ValidationErrors className={twCls(margin('mt-10'))} errors={customError} />
      <Button
        className={twCls(margin('mt-4'))}
        onClick={handleSubmit(verifyEmail)}
        disabled={formState.isSubmitting}
      >
        Verify
      </Button>
      <PopupDialog
        open={emailChanged}
        type="Information"
        message="Email has been changed"
        dismissAction={() => navigateAccount()}
      />
      <PopupDialog
        open={emailVerificationError}
        type="Error"
        message="Could not update or verify email address. Please contact admin for help."
        dismissAction={() => navigateAccount()}
      />
    </div>
  );
};
